import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { services } from '../mock';
import BackButton from '../components/BackButton';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

export default function Services() {
  const { t } = useTranslation();
  return (
    <>
      <SEO 
        title="Nos Services Béton Décoratif - Imprimé, Désactivé, Balayé | DumaExpertBéton"
        description="Béton imprimé, désactivé, balayé, lissé et ciré. Terrasses, allées, plages de piscine, parkings. Expert depuis 2014, zone PACA 65km."
        url="https://duma-expert-beton.fr/services"
      />

      <div className="fixed top-4 left-4 z-50">
        <BackButton />
      </div>
      
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 light:from-slate-50 light:via-white light:to-slate-50 transition-colors">
        {/* Hero Section */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#141823] dark:bg-[#141823] light:bg-slate-50 transition-colors">
          <div className="container mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <Badge className="mb-4 bg-[#E97C30] text-white">{t('services.title')}</Badge>
              <h1 className="text-4xl sm:text-5xl font-bold text-white dark:text-white light:text-slate-900 mb-6 transition-colors">
                {t('services.subtitle')}
              </h1>
              <p className="text-xl text-slate-100 dark:text-slate-100 light:text-slate-700 transition-colors">
                {t('services.description')}
              </p>
            </div>

            {/* Services Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => (
                <Card 
                  key={service.id} 
                  className="group hover:shadow-2xl transition-all duration-300 border overflow-hidden relative bg-gradient-to-br from-[#e8ba89] via-[#c97249] to-[#7ba89d] dark:from-[#e8ba89] dark:via-[#c97249] dark:to-[#7ba89d] light:from-slate-50 light:via-white light:to-slate-100 dark:border-white/10 light:border-slate-200 shadow-xl"
                >
                  <CardHeader>
                    <div className="mb-4">
                      <div className="w-14 h-14 bg-white/25 dark:bg-white/25 light:bg-orange-100 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg border border-white/20 dark:border-white/20 light:border-orange-200">
                        <div className="w-8 h-8 bg-white dark:bg-white light:bg-orange-500 rounded-lg shadow-inner"></div>
                      </div>
                    </div>
                    <CardTitle className="text-2xl text-[#E8F0F2] dark:text-[#E8F0F2] light:text-slate-900 font-extrabold drop-shadow-md tracking-tight transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-base text-[#E8F0F2]/90 dark:text-[#E8F0F2]/90 light:text-slate-700 font-medium transition-colors">
                      {service.description.substring(0, 150)}...
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-[#E8F0F2] dark:text-[#E8F0F2] light:text-green-600 drop-shadow-md mt-0.5 flex-shrink-0 transition-colors" />
                          <span className="text-[#E8F0F2]/90 dark:text-[#E8F0F2]/90 light:text-slate-700 font-medium transition-colors">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="pt-4 border-t border-white/25 dark:border-white/25 light:border-slate-300 shadow-sm">
                      <p className="text-sm font-semibold text-[#E8F0F2] dark:text-[#E8F0F2] light:text-slate-900 drop-shadow-sm mb-2 transition-colors">Applications:</p>
                      <div className="flex flex-wrap gap-2">
                        {service.applications.map((app, index) => (
                          <Badge 
                            key={index} 
                            variant="secondary" 
                            className="bg-white/20 dark:bg-white/20 light:bg-slate-200 text-[#E8F0F2] dark:text-[#E8F0F2] light:text-slate-700 border-white/30 dark:border-white/30 light:border-slate-300 backdrop-blur-md shadow-md font-medium transition-colors"
                          >
                            {app}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-700 to-teal-900 dark:from-teal-700 dark:to-teal-900 light:from-orange-100 light:to-slate-100 text-white dark:text-white light:text-slate-900 transition-colors">
          <div className="container mx-auto text-center max-w-4xl">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 transition-colors">
              {t('services.ctaTitle')}
            </h2>
            <p className="text-xl text-teal-100 dark:text-teal-100 light:text-slate-700 mb-8 transition-colors">
              {t('services.ctaSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/devis">
                <Button size="lg" className="bg-[#C97249] text-white hover:bg-[#B8624A] shadow-xl text-lg px-8 font-semibold">
                  💰 {t('services.calculate')}
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-white dark:border-white light:border-slate-900 text-white dark:text-white light:text-slate-900 hover:bg-white/10 dark:hover:bg-white/10 light:hover:bg-slate-200 transition-colors">
                  {t('services.immediateQuote')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
